import { Controller } from "@hotwired/stimulus";

/**
 * Reading Reviews Canvas Controller (Restaurant
 * Audit, Step 2).
 *
 * Drives the streaming-reveal of the "Lo que tus
 * clientes dicen *en serio*" step.
 *
 * Architecture (CAL-264). The reading_reviews canvas
 * is rendered ONCE on page load and never swapped by
 * `audit-stage`. All state is value-driven from
 * Stimulus values that the server updates via
 * `set_dataset_attributes` Turbo Stream actions. Pre-
 * rendered chip + review-card HTML chunks ship as
 * JSON-encoded arrays of strings on the dataset; we
 * just paste them into their slots.
 *
 * Lifecycle:
 *
 * 1. **connect()**: render whatever values are
 *    present at connect-time. If the payload was
 *    already on screen at page load (replay /
 *    already-done audit), the chip and review HTML
 *    arrays are non-empty: we paste them in
 *    immediately. If empty, the server-rendered
 *    skeletons stay put until narration arrives.
 *
 * 2. **`narratedValueChanged`**: fires when the
 *    payload arrives via dataset broadcast. We replace
 *    the chip slots and review columns with the
 *    pre-rendered HTML, then dispatch
 *    `audit-step:complete` so the Continue button
 *    enables.
 *
 * No time-based cascades. Animations from sub-
 * controllers (audit-reveal, audit-count-up) run
 * naturally on whatever HTML lands.
 */
export default class extends Controller {
  static values = {
    narrated: { type: String, default: "false" },
    insightChipsHtml: {
      type: Array,
      default: [],
    },
    reviewsHtml: {
      type: Array,
      default: [],
    },
    reviewCount: { type: Number, default: 0 },
  };

  static targets = [
    "insightChips",
    "reviewGridLeft",
    "reviewGridRight",
  ];

  connect() {
    this.rendered = false;
    this.maybeRender();
  }

  narratedValueChanged(value, previousValue) {
    // Stimulus fires every value-changed callback
    // once on connect with previousValue=undefined.
    // The connect() flow handles initial state.
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.maybeRender();
  }

  insightChipsHtmlValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    this.maybeRender();
  }

  reviewsHtmlValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    this.maybeRender();
  }

  // -------- render --------

  maybeRender() {
    if (this.rendered) return;
    if (this.narratedValue !== "true") return;

    this.renderInsightChips();
    this.renderReviewGrid();

    this.rendered = true;
    this.signalComplete();
  }

  renderInsightChips() {
    if (!this.hasInsightChipsTarget) return;
    const chunks = this.insightChipsHtmlValue;
    if (!chunks.length) return;

    this.insightChipsTarget.innerHTML = "";
    chunks.forEach((html) => {
      const wrapper = document.createElement("div");
      wrapper.className =
        "flex-1 min-w-[180px]";
      wrapper.innerHTML = html;
      this.insightChipsTarget.appendChild(wrapper);
    });
  }

  renderReviewGrid() {
    if (
      !this.hasReviewGridLeftTarget ||
      !this.hasReviewGridRightTarget
    ) {
      return;
    }
    const chunks = this.reviewsHtmlValue;
    if (!chunks.length) return;

    this.reviewGridLeftTarget.innerHTML = "";
    this.reviewGridRightTarget.innerHTML = "";

    chunks.forEach((html, idx) => {
      const wrapper = document.createElement("div");
      wrapper.innerHTML = html;
      const card = wrapper.firstElementChild;
      if (!card) return;
      const target =
        idx % 2 === 0
          ? this.reviewGridLeftTarget
          : this.reviewGridRightTarget;
      target.appendChild(card);
    });
  }

  // -------- completion --------

  signalComplete() {
    // Stamp the parent stage element with a completion
    // flag BEFORE dispatching the event. Stimulus
    // controller connect order is non-deterministic:
    // if this controller connects and fires the event
    // before audit-continue-button attaches its
    // listener, the event is lost. The flag lets the
    // button's connect() see the completion regardless
    // of attach order.
    const stage = this.element.closest(
      '[data-audit-stage-target="stage"]'
    );
    if (stage) {
      stage.dataset.auditStepComplete = "true";
    }

    document.dispatchEvent(
      new CustomEvent("audit-step:complete", {
        detail: {stepKey: "reading_reviews"},
      })
    );
  }
}
